/**
 * ProblemListPanel — Phase 1 (June 2026).
 *
 * A persistent, patient-scoped Problem List, distinct from the current-visit
 * complaint. Deterministically curated server-side (no LLM); this panel only
 * renders it and lets a clinician add a self-reported historical event.
 *
 * Strictly informational — the backend NEVER feeds this into the triage path.
 */
import { useEffect, useState } from 'react';
import axios from 'axios';
import { ListTree, Plus, ShieldQuestion, CalendarClock, Layers, Info, ClipboardCheck, AlertTriangle, CheckCircle2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS_META = {
  active: { label: 'Active', chip: 'bg-orange-100 text-orange-800 border-orange-300' },
  chronic_stable: { label: 'Chronic · stable', chip: 'bg-blue-100 text-blue-800 border-blue-300' },
  resolved: { label: 'Resolved', chip: 'bg-slate-100 text-slate-600 border-slate-300' },
};

const CATEGORY_LABEL = {
  chronic_condition: 'Chronic condition',
  surgery: 'Surgery',
  diagnostic_event: 'Diagnostic event',
  other: 'Other',
};

function EntryCard({ entry }) {
  const s = STATUS_META[entry.status] || STATUS_META.resolved;
  const prov = entry.provenance || {};
  const seenCount = (prov.seen_in_encounters || []).length;
  return (
    <div className="border border-slate-200 rounded-lg p-3 bg-white" data-testid={`problem-entry-${entry.entry_id}`}>
      <div className="flex items-start justify-between gap-2 flex-wrap">
        <div className="font-bold text-slate-900 text-sm">{entry.label}</div>
        <div className="flex items-center gap-1.5 flex-wrap">
          <span className={`text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded border ${s.chip}`} data-testid={`problem-status-${entry.entry_id}`}>
            {s.label}
          </span>
          <span className="text-[10px] text-slate-500 border border-slate-200 rounded px-2 py-0.5">
            {CATEGORY_LABEL[entry.category] || entry.category}
          </span>
        </div>
      </div>
      {(entry.finding || entry.source_note) && (
        <div className="text-xs text-slate-700 mt-1 leading-relaxed">
          {entry.finding}
          {entry.source_note && <span className="text-slate-500 italic"> · source: {entry.source_note}</span>}
        </div>
      )}
      <div className="flex items-center gap-3 mt-1.5 text-[10px] text-slate-500 flex-wrap">
        {entry.onset_date && <span className="flex items-center gap-1"><CalendarClock className="w-3 h-3" /> {entry.onset_date}</span>}
        {seenCount > 0 && <span className="flex items-center gap-1"><Layers className="w-3 h-3" /> seen in {seenCount} encounter{seenCount === 1 ? '' : 's'}</span>}
        <span className="flex items-center gap-1 text-amber-700"><ShieldQuestion className="w-3 h-3" /> patient-reported · unverified</span>
      </div>
    </div>
  );
}

function AddEntryForm({ encounterId, onAdded }) {
  const [open, setOpen] = useState(false);
  const [label, setLabel] = useState('');
  const [category, setCategory] = useState('diagnostic_event');
  const [status, setStatus] = useState('resolved');
  const [onsetDate, setOnsetDate] = useState('');
  const [finding, setFinding] = useState('');
  const [sourceNote, setSourceNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);

  const submit = async () => {
    setErr(null);
    if (!label.trim()) { setErr('Enter a condition or event name.'); return; }
    setSaving(true);
    try {
      const resp = await axios.post(`${API}/api/patient/problem-list/entry`, {
        encounterId,
        label,
        category,
        status,
        onset_date: onsetDate,
        finding,
        source_note: sourceNote,
      });
      onAdded && onAdded(resp.data);
      setLabel(''); setFinding(''); setSourceNote(''); setOnsetDate('');
      setOpen(false);
    } catch (e) {
      setErr(e.response?.data?.detail || e.message || 'Could not add entry');
    } finally {
      setSaving(false);
    }
  };

  if (!open) {
    return (
      <button onClick={() => setOpen(true)} data-testid="problem-list-add-toggle"
        className="mt-3 flex items-center gap-1.5 text-xs font-bold text-slate-700 border border-slate-300 rounded px-3 py-1.5 hover:bg-slate-50 transition-colors">
        <Plus className="w-3.5 h-3.5" /> Add historical event (self-reported)
      </button>
    );
  }

  return (
    <div className="mt-3 border border-slate-300 rounded-lg p-3 bg-slate-50" data-testid="problem-list-add-form">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        <div className="sm:col-span-2">
          <label className="text-[11px] font-bold text-slate-800 block mb-0.5">Condition / event *</label>
          <input type="text" value={label} onChange={e => setLabel(e.target.value)} data-testid="problem-add-label"
            placeholder="e.g., Sleep study; Cholecystectomy; Hypertension"
            className="w-full text-xs border border-slate-300 rounded px-2 py-1 bg-white" />
        </div>
        <div>
          <label className="text-[11px] font-bold text-slate-800 block mb-0.5">Type</label>
          <select value={category} onChange={e => setCategory(e.target.value)} data-testid="problem-add-category"
            className="w-full text-xs border border-slate-300 rounded px-2 py-1 bg-white">
            <option value="chronic_condition">Chronic condition</option>
            <option value="surgery">Surgery</option>
            <option value="diagnostic_event">Diagnostic event</option>
            <option value="other">Other</option>
          </select>
        </div>
        <div>
          <label className="text-[11px] font-bold text-slate-800 block mb-0.5">Status</label>
          <select value={status} onChange={e => setStatus(e.target.value)} data-testid="problem-add-status"
            className="w-full text-xs border border-slate-300 rounded px-2 py-1 bg-white">
            <option value="active">Active</option>
            <option value="chronic_stable">Chronic · stable</option>
            <option value="resolved">Resolved</option>
          </select>
        </div>
        <div>
          <label className="text-[11px] font-bold text-slate-800 block mb-0.5">Date / year</label>
          <input type="text" value={onsetDate} onChange={e => setOnsetDate(e.target.value)} data-testid="problem-add-date"
            placeholder="e.g., 2023" className="w-full text-xs border border-slate-300 rounded px-2 py-1 bg-white" />
        </div>
        <div>
          <label className="text-[11px] font-bold text-slate-800 block mb-0.5">Source note</label>
          <input type="text" value={sourceNote} onChange={e => setSourceNote(e.target.value)} data-testid="problem-add-source"
            placeholder="e.g., City Hospital, Mar 2025" className="w-full text-xs border border-slate-300 rounded px-2 py-1 bg-white" />
        </div>
        <div className="sm:col-span-2">
          <label className="text-[11px] font-bold text-slate-800 block mb-0.5">Finding</label>
          <input type="text" value={finding} onChange={e => setFinding(e.target.value)} data-testid="problem-add-finding"
            placeholder="e.g., echo EF normal, RBBB noted benign" className="w-full text-xs border border-slate-300 rounded px-2 py-1 bg-white" />
        </div>
      </div>
      {err && <div className="text-xs text-red-700 mt-2 italic" data-testid="problem-add-error">{err}</div>}
      <div className="flex items-center gap-2 mt-2">
        <button onClick={submit} disabled={saving} data-testid="problem-add-submit"
          className="text-xs font-bold px-4 py-1.5 rounded bg-slate-900 text-white hover:bg-slate-800 disabled:opacity-50">
          {saving ? 'Saving…' : 'Add to problem list'}
        </button>
        <button onClick={() => setOpen(false)} data-testid="problem-add-cancel"
          className="text-xs text-slate-500 hover:text-slate-700">Cancel</button>
      </div>
    </div>
  );
}

const SEV_META = {
  CRITICAL: 'bg-red-100 text-red-800 border-red-300',
  HIGH: 'bg-orange-100 text-orange-800 border-orange-300',
};

function ResultFollowupSection({ encounterId }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(null);

  const load = () => {
    if (!encounterId) return;
    axios.get(`${API}/api/patient/result-followup?encounterId=${encounterId}`)
      .then(r => setData(r.data)).catch(() => setData(null));
  };
  useEffect(load, [encounterId]);

  const resolve = async (item_id) => {
    setBusy(item_id);
    try {
      const r = await axios.post(`${API}/api/patient/result-followup/resolve`, { encounterId, item_id });
      setData(r.data);
    } catch (e) { /* best-effort */ } finally { setBusy(null); }
  };

  const items = data?.items || [];
  const open = items.filter(i => i.status === 'open');
  const done = items.filter(i => i.status === 'followed_up');
  if (!data || items.length === 0) return null;

  return (
    <div className="mt-4 pt-3 border-t border-slate-200" data-testid="result-followup-section">
      <div className="flex items-center gap-2 mb-1 flex-wrap">
        <ClipboardCheck className="w-4 h-4 text-slate-700" />
        <h3 className="text-sm font-bold text-slate-900">Abnormal-result follow-ups</h3>
        {open.length > 0 && (
          <span className="text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded border bg-red-100 text-red-800 border-red-300" data-testid="result-followup-open-count">
            {open.length} open loop{open.length === 1 ? '' : 's'}
          </span>
        )}
      </div>
      <div className="flex items-start gap-1.5 text-[11px] text-slate-500 italic mb-2 leading-relaxed">
        <Info className="w-3.5 h-3.5 shrink-0 mt-0.5" />
        <span>Longitudinal safety net for abnormal results that must not fall through the cracks. Advisory only — <strong className="text-slate-600">does not affect triage</strong>.</span>
      </div>
      <div className="space-y-2">
        {open.map(it => (
          <div key={it.item_id} className="border border-red-200 rounded-lg p-3 bg-red-50/50" data-testid={`result-followup-open-${it.item_id}`}>
            <div className="flex items-start justify-between gap-2 flex-wrap">
              <div className="flex items-center gap-1.5 font-bold text-slate-900 text-sm">
                <AlertTriangle className="w-3.5 h-3.5 text-red-600" /> {it.label}
              </div>
              <span className={`text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded border ${SEV_META[it.severity] || SEV_META.HIGH}`}>{it.severity}</span>
            </div>
            {it.detail && <div className="text-xs text-slate-700 mt-1 leading-relaxed">{it.detail}</div>}
            <button onClick={() => resolve(it.item_id)} disabled={busy === it.item_id}
              data-testid={`result-followup-resolve-${it.item_id}`}
              className="mt-2 flex items-center gap-1.5 text-[11px] font-bold text-emerald-800 border border-emerald-300 rounded px-3 py-1 hover:bg-emerald-50 disabled:opacity-50 transition-colors">
              <CheckCircle2 className="w-3.5 h-3.5" /> {busy === it.item_id ? 'Saving…' : 'Mark followed up'}
            </button>
          </div>
        ))}
        {done.map(it => (
          <div key={it.item_id} className="border border-slate-200 rounded-lg p-2.5 bg-slate-50 opacity-80" data-testid={`result-followup-done-${it.item_id}`}>
            <div className="flex items-center gap-1.5 text-xs text-slate-600">
              <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" />
              <span className="line-through">{it.label}</span>
              <span className="ml-auto text-[10px] uppercase tracking-wide text-emerald-700 font-bold">followed up</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function ProblemListPanel({ encounterId, initialProblemList }) {
  const [pl, setPl] = useState(initialProblemList || null);

  useEffect(() => {
    if (initialProblemList || !encounterId) return;
    axios.get(`${API}/api/patient/problem-list?encounterId=${encounterId}`)
      .then(r => setPl(r.data)).catch(() => setPl(null));
  }, [encounterId, initialProblemList]);

  const entries = pl?.entries || [];
  const grouped = {
    active: entries.filter(e => e.status === 'active'),
    chronic_stable: entries.filter(e => e.status === 'chronic_stable'),
    resolved: entries.filter(e => e.status === 'resolved'),
  };

  return (
    <section className="bg-white rounded-lg border-2 border-slate-300 p-4" data-testid="problem-list-panel">
      <div className="flex items-center gap-2 mb-1 flex-wrap">
        <ListTree className="w-5 h-5 text-slate-700" />
        <h2 className="text-base font-bold text-slate-900">Problem List</h2>
        <span className="text-[10px] uppercase tracking-wider text-slate-600 bg-slate-100 border border-slate-300 px-2 py-0.5 rounded font-bold">
          persistent across visits
        </span>
        <span className="text-[10px] uppercase tracking-wider text-emerald-700 bg-emerald-50 border border-emerald-300 px-2 py-0.5 rounded font-bold ml-auto">
          Deterministic · 0 LLM
        </span>
      </div>
      <div className="flex items-start gap-1.5 text-[11px] text-slate-500 italic mb-3 leading-relaxed">
        <Info className="w-3.5 h-3.5 shrink-0 mt-0.5" />
        <span>
          Significant health history carried forward across encounters, separate from this visit's complaint.
          Informational only — <strong className="text-slate-600">does not affect triage</strong>. Patient-reported, unverified.
        </span>
      </div>

      {entries.length === 0 ? (
        <div className="text-xs text-slate-500 border border-dashed border-slate-300 rounded-lg p-3" data-testid="problem-list-empty">
          No persistent problems catalogued yet for this patient. Recognised chronic conditions, surgeries, and diagnostic
          events are added automatically as encounters accrue; you can also add a self-reported historical event below.
          {pl && pl.eligible === false && (
            <span className="block mt-1 text-slate-400">
              (This encounter isn't linked to an identified patient — a self-reported entry needs a patient identity.)
            </span>
          )}
        </div>
      ) : (
        <div className="space-y-3">
          {['active', 'chronic_stable', 'resolved'].map(status => (
            grouped[status].length > 0 && (
              <div key={status} data-testid={`problem-group-${status}`}>
                <div className="text-[11px] font-bold uppercase tracking-wide text-slate-500 mb-1">
                  {STATUS_META[status].label} ({grouped[status].length})
                </div>
                <div className="space-y-2">
                  {grouped[status].map(e => <EntryCard key={e.entry_id} entry={e} />)}
                </div>
              </div>
            )
          ))}
        </div>
      )}

      <AddEntryForm encounterId={encounterId} onAdded={setPl} />
      <ResultFollowupSection encounterId={encounterId} />
    </section>
  );
}
